import React, { useCallback, useEffect, useLayoutEffect, useState } from "react";

// Visite guidée animée : un halo et une main pointent chaque bouton, une bulle
// explique à quoi il sert. Lancée au premier accès, relancée par le bouton « ? ».
// Les étapes dont le bouton n'est pas affiché (téléphone, rôle) sont sautées.

export const cleVisite = (espace, user) => `ql_visite_${espace}_${user?.id || user?.username || "anonyme"}`;

export function visiteDejaVue(espace, user) {
  try { return !!localStorage.getItem(cleVisite(espace, user)); } catch { return true; }
}

function visible(element) {
  if (!element) return false;
  const r = element.getBoundingClientRect();
  if (r.width < 2 || r.height < 2) return false;
  const style = window.getComputedStyle(element);
  return style.visibility !== "hidden" && style.display !== "none" && r.right > 0 && r.left < window.innerWidth;
}

// Défilement horizontal de la page et des zones qui contiennent l'élément.
function positionsHorizontales(element) {
  const zones = [document.scrollingElement || document.documentElement, document.body];
  for (let el = element?.parentElement; el; el = el.parentElement) zones.push(el);
  return zones.filter(Boolean).map((zone) => [zone, zone.scrollLeft]);
}

// Sur iPhone, amener un bouton à l'écran ne doit jamais décaler la page vers la gauche :
// seul le défilement vertical est conservé.
export function amenerALecran(element) {
  if (!element?.scrollIntoView) return;
  const avant = positionsHorizontales(element);
  element.scrollIntoView({ block: "nearest", inline: "nearest" });
  avant.forEach(([zone, gauche]) => { if (zone.scrollLeft !== gauche) zone.scrollLeft = gauche; });
}

// Fin de la visite : la page reprend sa place, sans décalage horizontal ni zoom. Sur iPhone, deux
// touchers rapides sur « Suivant » font zoomer Safari ; limiter un instant l'échelle à 1 le ramène à 100 %.
export function recentrerPage() {
  if (window.scrollX) window.scrollTo(0, window.scrollY);
  [document.scrollingElement, document.documentElement, document.body].forEach((zone) => { if (zone?.scrollLeft) zone.scrollLeft = 0; });
  const meta = document.querySelector('meta[name="viewport"]');
  if (meta && (window.visualViewport?.scale || 1) > 1.01) {
    const contenu = meta.getAttribute("content") || "";
    meta.setAttribute("content", `${contenu.replace(/,?\s*maximum-scale=[^,]*/g, "")}, maximum-scale=1`);
    setTimeout(() => meta.setAttribute("content", contenu), 400);
  }
}

function trouver(selecteur) {
  return Array.from(document.querySelectorAll(selecteur)).find(visible) || null;
}

export default function VisiteGuidee({ etapes, espace, user, onClose }) {
  const [disponibles, setDisponibles] = useState([]);
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);

  // Ne garder que les étapes dont le bouton est réellement à l'écran.
  useEffect(() => {
    const minuterie = setTimeout(() => setDisponibles(etapes.filter((e) => trouver(e.cible))), 250);
    return () => clearTimeout(minuterie);
  }, [etapes]);

  const etape = disponibles[index];

  const mesurer = useCallback(() => {
    if (!etape) return;
    const element = trouver(etape.cible);
    if (!element) return;
    const r = element.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [etape]);

  useLayoutEffect(() => {
    if (!etape) return undefined;
    const element = trouver(etape.cible);
    amenerALecran(element);
    mesurer();
    window.addEventListener("resize", mesurer);
    window.addEventListener("scroll", mesurer, true);
    return () => {
      window.removeEventListener("resize", mesurer);
      window.removeEventListener("scroll", mesurer, true);
    };
  }, [etape, mesurer]);

  const terminer = useCallback(() => {
    try { localStorage.setItem(cleVisite(espace, user), new Date().toISOString()); } catch { /* stockage indisponible */ }
    recentrerPage();
    onClose?.();
  }, [espace, user, onClose]);

  useEffect(() => {
    const touche = (e) => {
      if (e.key === "Escape") terminer();
      if (e.key === "ArrowRight") setIndex((i) => Math.min(i + 1, disponibles.length - 1));
      if (e.key === "ArrowLeft") setIndex((i) => Math.max(i - 1, 0));
    };
    document.addEventListener("keydown", touche);
    return () => document.removeEventListener("keydown", touche);
  }, [terminer, disponibles.length]);

  if (!disponibles.length) return null;
  if (!etape || !rect) return null;

  const marge = 6;
  const halo = { top: rect.top - marge, left: rect.left - marge, width: rect.width + marge * 2, height: rect.height + marge * 2 };
  const largeurBulle = Math.min(320, window.innerWidth - 24);
  const enBas = rect.top + rect.height + 190 < window.innerHeight || rect.top < 200;
  const aDroite = rect.left + rect.width + largeurBulle + 24 < window.innerWidth && rect.height > window.innerHeight * 0.25;
  const bulle = aDroite
    ? { top: Math.max(12, Math.min(rect.top, window.innerHeight - 220)), left: rect.left + rect.width + 18 }
    : {
      top: enBas ? Math.min(rect.top + rect.height + 18, window.innerHeight - 200) : Math.max(12, rect.top - 190),
      left: Math.max(12, Math.min(rect.left + rect.width / 2 - largeurBulle / 2, window.innerWidth - largeurBulle - 12)),
    };
  // La main reste dans l'écran, même pour un bouton collé au bord droit.
  const main = { top: rect.top + rect.height * 0.55, left: Math.min(rect.left + rect.width * 0.7, window.innerWidth - 44) };
  const derniere = index === disponibles.length - 1;

  return (
    <div className="ql-visite" role="dialog" aria-modal="true" aria-label="Visite guidée">
      <div className="ql-visite-fond" onClick={() => (derniere ? terminer() : setIndex(index + 1))} />
      <div className="ql-visite-halo" style={halo} />
      <i className="bi bi-hand-index-thumb-fill ql-visite-main" style={main} aria-hidden="true"></i>
      <div className="ql-visite-bulle" style={bulle} key={index}>
        <h4><i className={`bi ${etape.icone || "bi-lightbulb-fill"}`}></i>{etape.titre}</h4>
        <p>{etape.texte}</p>
        <div className="ql-visite-pied">
          <div className="ql-visite-points" aria-label={`Étape ${index + 1} sur ${disponibles.length}`}>
            {disponibles.map((e, i) => <span key={e.cible} className={i === index ? "actif" : ""} />)}
          </div>
          <div className="d-flex gap-1">
            <button type="button" className="btn btn-sm btn-link text-muted" onClick={terminer}>Passer</button>
            {index > 0 && <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setIndex(index - 1)}>Précédent</button>}
            <button type="button" className="btn btn-sm btn-primary" onClick={() => (derniere ? terminer() : setIndex(index + 1))}>
              {derniere ? "C'est parti !" : "Suivant"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
